import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
import { backoffDelay } from './utils/backoff';

const DEFAULTS = {
  url: '/chats/ws',
  heartbeat: 10000,
  connectTimeout: 15000,
  baseReconnectDelay: 1000,
  maxReconnectDelay: 30000,
};

/**
 * Обёртка над SockJS + STOMP с автопереподключением.
 *
 * Отличия от прежней инлайн-реализации в App.vue:
 *  - экспоненциальный backoff с jitter и потолком (не запрос в секунду);
 *  - таймаут установки соединения (лечит «залип в connecting»);
 *  - single-flight: параллельные connect() не создают несколько сокетов;
 *  - таймер переподключения хранится и гасится в disconnect();
 *  - при реконнекте автоматически восстанавливаются все активные подписки.
 */
export default class WebSocketService {
  constructor(options = {}) {
    this.options = { ...DEFAULTS, ...options };
    this.client = null;
    this.state = 'idle'; // idle | connecting | connected | offline
    this.manualClose = false;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.connectTimer = null;
    this.subscriptions = new Map(); // topic -> { callback, sub }
    this.listeners = { connect: [], disconnect: [] };
  }

  on(event, fn) {
    if (this.listeners[event]) this.listeners[event].push(fn);
    return this;
  }

  _emit(event, payload) {
    (this.listeners[event] || []).forEach((fn) => {
      try {
        fn(payload);
      } catch (e) {
        console.error(`[WS] listener "${event}" failed:`, e);
      }
    });
  }

  isConnected() {
    return this.state === 'connected' && this.client && this.client.connected;
  }

  connect() {
    if (this.state === 'connecting' || this.state === 'connected') return;
    this.manualClose = false;
    this._openSocket();
  }

  _openSocket() {
    this._clearTimers();
    this.state = 'connecting';

    let socket;
    try {
      socket = new SockJS(this.options.url);
    } catch (e) {
      console.error('[WS] SockJS init failed:', e);
      this._scheduleReconnect();
      return;
    }

    const client = Stomp.over(socket);
    this.client = client;
    client.debug = process.env.NODE_ENV === 'production' ? null : client.debug;
    client.heartbeat.outgoing = this.options.heartbeat;
    client.heartbeat.incoming = this.options.heartbeat;

    // Таймаут: если за connectTimeout ни onConnect, ни onError — считаем попытку провалившейся.
    this.connectTimer = setTimeout(() => {
      console.warn('[WS] connect timeout, retrying');
      this._teardownClient();
      this._handleDrop();
    }, this.options.connectTimeout);

    client.connect(
      {},
      () => {
        if (this.client !== client) return; // устаревший колбэк
        this._clearTimers();
        this.state = 'connected';
        this.reconnectAttempts = 0;
        this._resubscribeAll();
        this._emit('connect');
      },
      (error) => {
        if (this.client !== client) return;
        console.error('[WS] connection error:', error);
        this._teardownClient();
        this._handleDrop();
      }
    );
  }

  _handleDrop() {
    this.state = 'offline';
    // подписки на упавшем сокете мертвы — пометим для переподписки при реконнекте
    this.subscriptions.forEach((entry) => {
      entry.sub = null;
    });
    this._emit('disconnect');
    if (!this.manualClose) this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (this.manualClose || this.reconnectTimer) return;
    const attempt = this.reconnectAttempts++;
    const delay = backoffDelay(
      attempt,
      this.options.baseReconnectDelay,
      this.options.maxReconnectDelay
    );
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._openSocket();
    }, delay);
  }

  /** Немедленная попытка переподключения (возврат сети / вкладки). */
  reconnectNow() {
    if (this.manualClose) return;
    if (this.state === 'connecting' || this.state === 'connected') return;
    this._clearTimers();
    this.reconnectAttempts = 0;
    this._openSocket();
  }

  subscribe(topic, callback) {
    const existing = this.subscriptions.get(topic);
    if (existing) {
      existing.callback = callback;
    } else {
      this.subscriptions.set(topic, { callback, sub: null });
    }
    if (this.isConnected()) this._doSubscribe(topic);
  }

  _doSubscribe(topic) {
    const entry = this.subscriptions.get(topic);
    if (!entry || entry.sub) return; // уже подписаны
    try {
      entry.sub = this.client.subscribe(topic, (frame) => entry.callback(frame));
    } catch (e) {
      console.error(`[WS] subscribe ${topic} failed:`, e);
    }
  }

  _resubscribeAll() {
    this.subscriptions.forEach((_, topic) => this._doSubscribe(topic));
  }

  unsubscribe(topic) {
    const entry = this.subscriptions.get(topic);
    if (!entry) return;
    if (entry.sub) {
      try {
        entry.sub.unsubscribe();
      } catch (e) {
        /* сокет уже мёртв */
      }
    }
    this.subscriptions.delete(topic);
  }

  /**
   * @returns {boolean} true — фрейм отправлен, false — нет соединения.
   */
  send(destination, body, headers = {}) {
    if (!this.isConnected()) return false;
    try {
      this.client.send(destination, headers, body);
      return true;
    } catch (e) {
      console.error('[WS] send failed:', e);
      return false;
    }
  }

  _teardownClient() {
    if (!this.client) return;
    try {
      this.client.disconnect();
    } catch (e) {
      /* игнорируем */
    }
    this.client = null;
  }

  _clearTimers() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  /** Полная и окончательная остановка: гасит переподключение. */
  disconnect() {
    this.manualClose = true;
    this._clearTimers();
    this.subscriptions.forEach((entry) => {
      if (entry.sub) {
        try {
          entry.sub.unsubscribe();
        } catch (e) {
          /* игнорируем */
        }
      }
    });
    this.subscriptions.clear();
    this._teardownClient();
    this.state = 'idle';
  }
}
